/**
 * Smoothly scrolls to the top of the page
 * @param behavior - The scroll behavior ('smooth' or 'auto')
 * @param duration - Custom duration for the scroll animation (optional)
 */
export const scrollToTop = (
  behavior: ScrollBehavior = 'smooth',
  duration?: number
): void => {
  if (duration && behavior === 'smooth') {
    // Custom smooth scroll with specified duration
    const startPosition = window.pageYOffset;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out cubic)
      const ease = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, startPosition * (1 - ease));

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  } else {
    // Use browser's native smooth scroll
    window.scrollTo({
      top: 0,
      left: 0,
      behavior,
    });
  }
};

/**
 * Scrolls to a specific element smoothly
 * @param elementId - The ID of the element to scroll to
 * @param behavior - The scroll behavior ('smooth' or 'auto')
 * @param offset - Optional offset from the element (useful for fixed headers)
 */
export const scrollToElement = (
  elementId: string,
  behavior: ScrollBehavior = 'smooth',
  offset: number = 0
): void => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition = element.offsetTop - offset;
    window.scrollTo({
      top: elementPosition,
      left: 0,
      behavior,
    });
  }
};
